/**
 * Deterministic seeded random number generator.
 *
 * THIS REPLACES Math.random() EVERYWHERE IN GAME LOGIC.
 *
 * Why: deterministic RNG enables anti-cheat (server replays runs from seed +
 * actions, recomputes results, rejects mismatches), daily challenges (all
 * players share one seed for fair competition), reproducible bugs (any state
 * derives from seed + action log), and lockstep co-op (clients agree by
 * computing identical state).
 *
 * Algorithm: xorshift128+ — fast, well-distributed, passes BigCrush. Not
 * cryptographically secure (it shouldn't be — players need to trust seeds
 * to be reproducible).
 *
 * Forking: child streams isolated from parent. Combat, loot, and procedural
 * map generation each get their own stream so changing one system's call
 * pattern doesn't shift outcomes in unrelated systems.
 */

export class SeededRng {
  private s0: bigint;
  private s1: bigint;

  constructor(seed: number | bigint) {
    // Splitmix64 seeding — convert single seed to two well-distributed 64-bit states
    let s = BigInt(seed) & 0xffffffffffffffffn;
    s = (s + 0x9e3779b97f4a7c15n) & 0xffffffffffffffffn;
    s = ((s ^ (s >> 30n)) * 0xbf58476d1ce4e5b9n) & 0xffffffffffffffffn;
    s = ((s ^ (s >> 27n)) * 0x94d049bb133111ebn) & 0xffffffffffffffffn;
    s = s ^ (s >> 31n);
    this.s0 = s & 0xffffffffffffffffn;

    s = (s + 0x9e3779b97f4a7c15n) & 0xffffffffffffffffn;
    s = ((s ^ (s >> 30n)) * 0xbf58476d1ce4e5b9n) & 0xffffffffffffffffn;
    s = ((s ^ (s >> 27n)) * 0x94d049bb133111ebn) & 0xffffffffffffffffn;
    s = s ^ (s >> 31n);
    this.s1 = s & 0xffffffffffffffffn;

    // Ensure non-zero state
    if (this.s0 === 0n && this.s1 === 0n) {
      this.s0 = 1n;
    }
  }

  /** Raw 64-bit unsigned integer. */
  nextU64(): bigint {
    let s1 = this.s0;
    const s0 = this.s1;
    const result = (s0 + s1) & 0xffffffffffffffffn;
    this.s0 = s0;
    s1 ^= (s1 << 23n) & 0xffffffffffffffffn;
    this.s1 = (s1 ^ s0 ^ (s1 >> 17n) ^ (s0 >> 26n)) & 0xffffffffffffffffn;
    return result;
  }

  /** Float in [0, 1). */
  next(): number {
    // Use top 53 bits for max precision in a JS number
    const u = this.nextU64() >> 11n;
    return Number(u) / 0x20000000000000;
  }

  /** Integer in [min, max] inclusive. */
  nextInt(min: number, max: number): number {
    if (max < min) throw new Error(`SeededRng.nextInt: max (${max}) < min (${min})`);
    const range = max - min + 1;
    return min + Math.floor(this.next() * range);
  }

  /** Roll a chance check. p in [0, 1]. Returns true with probability p. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  /** Pick a random element from an array. Throws on empty array. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('SeededRng.pick: empty array');
    const item = items[this.nextInt(0, items.length - 1)];
    if (item === undefined) throw new Error('SeededRng.pick: item undefined (impossible)');
    return item;
  }

  /** Weighted pick. weights array must match items length. */
  pickWeighted<T>(items: readonly T[], weights: readonly number[]): T {
    if (items.length === 0) throw new Error('SeededRng.pickWeighted: empty array');
    if (items.length !== weights.length) {
      throw new Error(
        `SeededRng.pickWeighted: items (${items.length}) and weights (${weights.length}) length mismatch`,
      );
    }
    let total = 0;
    for (const w of weights) {
      if (w < 0) throw new Error('SeededRng.pickWeighted: negative weight');
      total += w;
    }
    if (total <= 0) throw new Error('SeededRng.pickWeighted: total weight is zero');
    const roll = this.next() * total;
    let acc = 0;
    for (let i = 0; i < items.length; i++) {
      acc += weights[i] ?? 0;
      if (roll < acc) {
        const item = items[i];
        if (item === undefined) throw new Error('SeededRng.pickWeighted: item undefined');
        return item;
      }
    }
    // Floating-point safety net
    const last = items[items.length - 1];
    if (last === undefined) throw new Error('SeededRng.pickWeighted: last undefined');
    return last;
  }

  /** Shuffle array in place using Fisher-Yates. Returns same array. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i);
      const a = items[i];
      const b = items[j];
      if (a !== undefined && b !== undefined) {
        items[i] = b;
        items[j] = a;
      }
    }
    return items;
  }

  /**
   * Fork a child RNG stream. Child is deterministic from parent state but
   * advances independently. Use this to isolate subsystems (combat, loot,
   * map gen) so their call patterns don't interfere.
   */
  fork(label: string): SeededRng {
    // Hash the label into the seed so different child labels produce different streams
    let h = 0xcbf29ce484222325n;
    for (let i = 0; i < label.length; i++) {
      h = (h ^ BigInt(label.charCodeAt(i))) & 0xffffffffffffffffn;
      h = (h * 0x100000001b3n) & 0xffffffffffffffffn;
    }
    const childSeed = (this.nextU64() ^ h) & 0xffffffffffffffffn;
    return new SeededRng(childSeed);
  }

  /** Snapshot internal state for save/replay. */
  serialize(): { s0: string; s1: string } {
    return { s0: this.s0.toString(), s1: this.s1.toString() };
  }

  /** Restore from a snapshot. */
  static deserialize(snapshot: { s0: string; s1: string }): SeededRng {
    const rng = new SeededRng(0);
    rng.s0 = BigInt(snapshot.s0);
    rng.s1 = BigInt(snapshot.s1);
    if (rng.s0 === 0n && rng.s1 === 0n) rng.s0 = 1n;
    return rng;
  }
}
